/* Copyright 2016-2022
   Kabandy
   Author: Cedric Hoarau
 */

import { ApiDataModel, ApiRequestBody } from "../ApiDataModel";
import { ContentType } from "../Network";
import { ModelDataModel, QuantityDataModel } from "../DataModel";
import { Fetch, FetchJson } from "./FetchHelper";
import { ApiRouteFormatter, getApiVersion, isCloudRunEnabled } from ".";

type IApiQuantity = ApiDataModel.IQuantity;
type IApiVersion = ApiDataModel.IVersion;
type ModelStorageFileUrlMap = ModelDataModel.Storage.IFileUrlMap;
type IApiQuantityPostRequestBody = ApiRequestBody.Quantity.IPost;
type QuantityStorageFileUrlMap = QuantityDataModel.Storage.FileUrlMap;
type IApiModel = ApiDataModel.IModel
type IApiBoard = ApiDataModel.IBoard;
type IApiBoardCell = ApiDataModel.IBoardCell;
type IApiBoardGroup = ApiDataModel.IBoardGroup;
type IApiBoardItem = ApiDataModel.IBoardItem;
type IApiBoardProperty = ApiDataModel.IBoardProperty;
type IApiBoardSection = ApiDataModel.IBoardSection;
type IApiBoardStatus = ApiDataModel.IBoardStatus;
type IApiBoardPostRequestBody = ApiRequestBody.Board.IPost
type IApiBoardPutRequestBody = ApiRequestBody.Board.IPut

enum EHeaders {
    Authorization = "authorization",
    ContentType = "Content-Type",
    XApiKey = "x-api-key"
}

enum EMethod {
    Delete = "DELETE",
    Get = "GET",
    Post = "POST",
    Put = "PUT"
}

// GET https://api.kabandy.com/v1/workspaces/:workspaceId/models/:modelId
export async function GetMeWorkspaceModel(apiKey: string, accessToken: string, workspaceId: string, modelId: string): Promise<IApiModel> {
    const { model }: { model: IApiModel } = await FetchJson(
        EMethod.Get,
        ApiRouteFormatter.FormatMeModelUrl(isCloudRunEnabled(), getApiVersion(), workspaceId, modelId),
        _CreateApiRequestHeaders(apiKey, accessToken)
    );
    return model;
}

//
// --- Workspace's version (with files) and quantities (with files)

// GET https://api.kabandy.com/v1/workspaces/:workspaceId/versions/:versionId
export function GetMeWorkspaceVersions(apiKey: string, accessToken: string, workspaceId: string, versionIds: string[] = []): Promise<IApiVersion[]> {
    let promises: Promise<IApiVersion>[] = [];

    for (let versionId of versionIds) {
        promises.push(GetMeWorkspaceVersion(apiKey, accessToken, workspaceId, versionId));
    }

    return Promise.all(promises);
}
export async function GetMeWorkspaceVersion(apiKey: string, accessToken: string, workspaceId: string, versionId: string): Promise<IApiVersion> {
    const { version }: { version: IApiVersion } = await FetchJson(
        EMethod.Get,
        ApiRouteFormatter.FormatMeWorkspaceVersionUrl(isCloudRunEnabled(), getApiVersion(), workspaceId, versionId),
        _CreateApiRequestHeaders(apiKey, accessToken)
    );
    return version;
}
// GET https://api.kabandy.com/v1/workspaces/:workspaceId/versions/:versionId/files
export async function GetMeWorkspaceVersionFiles(
    apiKey: string,
    accessToken: string,
    workspaceId: string,
    versionId: string
): Promise<ModelStorageFileUrlMap> {
    const { visuFileUrlMap }: { visuFileUrlMap: ModelStorageFileUrlMap } = await FetchJson(
        EMethod.Get,
        ApiRouteFormatter.FormatMeWorkspaceVersionFilesUrl(isCloudRunEnabled(), getApiVersion(), workspaceId, versionId),
        _CreateApiRequestHeaders(apiKey, accessToken)
    );
    return visuFileUrlMap;
}

// GET https://api.kabandy.com/v1/workspaces/:workspaceId/quantities?quantityType=QUANTITY_TYPE
export async function GetMeQuantities(
    apiKey: string,
    accessToken: string,
    workspaceId: string,
    quantityType: QuantityDataModel.EQuantityType
): Promise<IApiQuantity[]> {
    const url: string = ApiRouteFormatter.FormatMeQuantitiesUrl(isCloudRunEnabled(), getApiVersion(), workspaceId) + `?quantityType=${quantityType}`;
    const { quantities }: { quantities: IApiQuantity[] } = await FetchJson(EMethod.Get, url, _CreateApiRequestHeaders(apiKey, accessToken));
    return quantities;
}
// GET https://api.kabandy.com/v1/workspaces/:workspaceId/quantities/:quantityId?quantityType=QUANTITY_TYPE
export async function GetMeQuantity(
    apiKey: string,
    accessToken: string,
    workspaceId: string,
    quantityId: string,
    quantityType: QuantityDataModel.EQuantityType
): Promise<IApiQuantity> {
    const url: string =
        ApiRouteFormatter.FormatMeQuantityUrl(isCloudRunEnabled(), getApiVersion(), workspaceId, quantityId) + `?quantityType=${quantityType}`;
    const { quantity }: { quantity: IApiQuantity } = await FetchJson(EMethod.Get, url, _CreateApiRequestHeaders(apiKey, accessToken));
    return quantity;
}
// DELETE https://api.kabandy.com/v1/workspaces/:workspaceId/quantities/:quantityId?quantityType=QUANTITY_TYPE
export async function DeleteMeQuantity(
    apiKey: string,
    accessToken: string,
    workspaceId: string,
    quantityId: string,
    quantityType: QuantityDataModel.EQuantityType
): Promise<void> {
    const url: string =
        ApiRouteFormatter.FormatMeQuantityUrl(isCloudRunEnabled(), getApiVersion(), workspaceId, quantityId) + `?quantityType=${quantityType}`;
    await FetchJson(EMethod.Delete, url, _CreateApiRequestHeaders(apiKey, accessToken));
}
// POST https://api.kabandy.com/v1/workspaces/:workspaceId/quantities
export async function PostMeQuantities(
    apiKey: string,
    accessToken: string,
    workspaceId: string,
    body: IApiQuantityPostRequestBody
): Promise<IApiQuantity> {
    const { quantity }: { quantity: IApiQuantity } = await FetchJson(
        EMethod.Post,
        ApiRouteFormatter.FormatMeQuantitiesUrl(isCloudRunEnabled(), getApiVersion(), workspaceId),
        _CreateApiRequestHeaders(apiKey, accessToken),
        JSON.stringify(body)
    );
    return quantity;
}
// GET https://api.kabandy.com/v1/workspaces/:workspaceId/quantities/:quantityId/files?quantityType=QUANTITY_TYPE
export async function GetMeQuantityFiles(
    apiKey: string,
    accessToken: string,
    workspaceId: string,
    quantityId: string,
    quantityType: QuantityDataModel.EQuantityType
): Promise<QuantityStorageFileUrlMap> {
    const url: string =
        ApiRouteFormatter.FormatMeQuantityFilesUrl(isCloudRunEnabled(), getApiVersion(), workspaceId, quantityId) + `?quantityType=${quantityType}`;
    const { visuFileUrlMap }: { visuFileUrlMap: ModelStorageFileUrlMap } = await FetchJson(
        EMethod.Get,
        url,
        _CreateApiRequestHeaders(apiKey, accessToken)
    );
    return visuFileUrlMap;
}

//
// --- Upload file

// Post - https://api.kabandy.com/v1/files/upload - Upload a file
export async function PostFilesUpload(
    apiKey: string,
    accessToken: string,
    filename: string,
    contentType?: string,
    contentLength?: number
): Promise<{ id: string; sessionUri: string }> {
    let headers: any = _CreateApiRequestHeaders(apiKey, accessToken);
    if (contentType) {
        headers["x-upload-content-type"] = contentType;
    }
    if (contentLength != undefined) {
        headers["x-upload-content-length"] = contentLength;
    }
    const bodyAsStr: string = JSON.stringify({ filename: filename });
    const url: string = ApiRouteFormatter.FormatFilesUploadUrl(isCloudRunEnabled(), getApiVersion());
    const response: any = await Fetch(EMethod.Post, url, headers, bodyAsStr);
    return response.json();
}
// Put - https://storage.googleapis.com/upload/storage/v1/b/${bucket}/o?uploadType=resumable&name=tmp/${fileId}/${filename}&upload_id=ADPycduGKP7b_EgygrnrGRhB2swUOa11xU4we0rwixNxYymBOIJIXFzvxmljwPfL6VLAzyIiHC9q-I0ioJ9YuUyz8ws0SA
export async function PutFilesUpload(sessionUri: string, buffer: ArrayBuffer, contentLength?: number): Promise<any> {
    let headers: any = {
        "content-type": ContentType.Json
    };

    if (contentLength != undefined) {
        headers["content-length"] = contentLength;
    }

    const response: any = await Fetch(EMethod.Put, sessionUri, headers, buffer);
    return response.json();
}

export async function GetFilesFromVersions(
    apiKey: string,
    accessToken: string,
    versions: IApiVersion[],
    ioVersionFileUrlMap: { [key: string]: ModelStorageFileUrlMap } = {}
): Promise<any> {
    let promises: Promise<void>[] = [];
    for (let version of versions) {
        promises.push(_GetFilesFromVersion(apiKey, accessToken, version, ioVersionFileUrlMap));
    }
    await Promise.all(promises);

    return ioVersionFileUrlMap;
}
async function _GetFilesFromVersion(
    apiKey: string,
    accessToken: string,
    version: IApiVersion,
    ioVersionFileUrlMap: { [key: string]: ModelStorageFileUrlMap } = {}
): Promise<void> {
    const fileUrlMap: ModelStorageFileUrlMap = await GetMeWorkspaceVersionFiles(
        apiKey,
        accessToken,
        version.workspaceId,
        version.id
    );
    ioVersionFileUrlMap[version.id] = fileUrlMap;
}

//
// --- Boards

// GET https://api.kabandy.com/v1/workspaces/:workspaceId/boards
export async function GetMeBoards(
    apiKey: string,
    accessToken: string,
    workspaceId: string
): Promise<IApiBoard[]> {
    const url: string = ApiRouteFormatter.FormatMeBoardsUrl(isCloudRunEnabled(), getApiVersion(), workspaceId);
    const { boards }: { boards: IApiBoard[] } = await FetchJson(EMethod.Get, url, _CreateApiRequestHeaders(apiKey, accessToken));
    return boards;
}
// GET https://api.kabandy.com/v1/workspaces/:workspaceId/boards/:boardId
export async function GetMeBoard(
    apiKey: string,
    accessToken: string,
    workspaceId: string,
    boardId: string
): Promise<IApiBoard> {
    const url: string =
        ApiRouteFormatter.FormatMeBoardUrl(isCloudRunEnabled(), getApiVersion(), workspaceId, boardId);
    const { board }: { board: IApiBoard } = await FetchJson(EMethod.Get, url, _CreateApiRequestHeaders(apiKey, accessToken));
    return board;
}
// POST https://api.kabandy.com/v1/workspaces/:workspaceId/boards
export async function PostMeBoards(
    apiKey: string,
    accessToken: string,
    workspaceId: string,
    body: IApiBoardPostRequestBody
): Promise<IApiBoard> {
    const { board }: { board: IApiBoard } = await FetchJson(
        EMethod.Post,
        ApiRouteFormatter.FormatMeBoardsUrl(isCloudRunEnabled(), getApiVersion(), workspaceId),
        _CreateApiRequestHeaders(apiKey, accessToken),
        JSON.stringify(body)
    );
    return board;
}
// PUT https://api.kabandy.com/v1/workspaces/:workspaceId/boards/:boardId
export async function PutMeBoard(
    apiKey: string,
    accessToken: string,
    workspaceId: string,
    boardId: string,
    body: IApiBoardPutRequestBody
): Promise<IApiBoard> {
    const { board }: { board: IApiBoard } = await FetchJson(
        EMethod.Put,
        ApiRouteFormatter.FormatMeBoardUrl(isCloudRunEnabled(), getApiVersion(), workspaceId, boardId),
        _CreateApiRequestHeaders(apiKey, accessToken),
        JSON.stringify(body)
    );
    return board;
}
// DELETE https://api.kabandy.com/v1/workspaces/:workspaceId/boards/:boardId
export async function DeleteMeBoard(
    apiKey: string,
    accessToken: string,
    workspaceId: string,
    boardId: string
): Promise<void> {
    const url: string =
        ApiRouteFormatter.FormatMeBoardUrl(isCloudRunEnabled(), getApiVersion(), workspaceId, boardId);
    await FetchJson(EMethod.Delete, url, _CreateApiRequestHeaders(apiKey, accessToken));
}

//
// --- Board status

// GET https://api.kabandy.com/v1/workspaces/:workspaceId/boards/:boardId/status
export function GetMeBoardStatuses(
    apiKey: string,
    accessToken: string,
    boards: IApiBoard[]
) : Promise<IApiBoardStatus[]> {
    let promises: Promise<IApiBoardStatus>[] = [];
    for (let board of boards) {
        promises.push(GetMeBoardStatus(apiKey, accessToken, board ));
    }
    return Promise.all(promises);
}
export async function GetMeBoardStatus(
    apiKey: string,
    accessToken: string,
    board: IApiBoard
) : Promise<IApiBoardStatus> {
    const url: string =
        ApiRouteFormatter.FormatMeBoardStatusUrl(isCloudRunEnabled(), getApiVersion(), board.workspaceId, board.id);
    const { status }: { status: IApiBoardStatus } = await FetchJson(EMethod.Get, url, _CreateApiRequestHeaders(apiKey, accessToken));
    return status;
}

//
// --- Board sections

// GET https://api.kabandy.com/v1/workspaces/:workspaceId/boards/:boardId/sections
export async function GetMeBoardSections(
    apiKey: string,
    accessToken: string,
    workspaceId: string,
    boardId: string
) : Promise<IApiBoardSection[]> {
    const url: string =
        ApiRouteFormatter.FormatMeBoardSectionsUrl(isCloudRunEnabled(), getApiVersion(), workspaceId, boardId);
    const { sections }: { sections: IApiBoardSection[] } = await FetchJson(EMethod.Get, url, _CreateApiRequestHeaders(apiKey, accessToken));
    return sections;
}

//
// --- Board groups

// GET https://api.kabandy.com/v1/workspaces/:workspaceId/boards/:boardId/sections/:sectionId/groups
export async function GetMeBoardSectionGroups(
    apiKey: string,
    accessToken: string,
    workspaceId: string,
    boardId: string,
    sectionId: string
) : Promise<IApiBoardGroup[]> {
    const url: string =
        ApiRouteFormatter.FormatMeBoardSectionGroupsUrl(isCloudRunEnabled(), getApiVersion(), workspaceId, boardId, sectionId);
    const { groups }: { groups: IApiBoardGroup[] } = await FetchJson(EMethod.Get, url, _CreateApiRequestHeaders(apiKey, accessToken));
    return groups;
}

//
// --- Board items

// GET https://api.kabandy.com/v1/workspaces/:workspaceId/boards/:boardId/sections/:sectionId/groups/:groupId/items
export async function GetMeBoardSectionGroupItems(
    apiKey: string,
    accessToken: string,
    workspaceId: string,
    boardId: string,
    sectionId: string,
    groupId: string
) : Promise<IApiBoardItem[]> {
    const url: string =
        ApiRouteFormatter.FormatMeBoardSectionGroupItemsUrl(isCloudRunEnabled(), getApiVersion(), workspaceId, boardId, sectionId, groupId);
    const { items }: { items: IApiBoardItem[] } = await FetchJson(EMethod.Get, url, _CreateApiRequestHeaders(apiKey, accessToken));
    return items;
}

//
// --- Board properties

// GET https://api.kabandy.com/v1/workspaces/:workspaceId/boards/:boardId/properties
export async function GetMeBoardProperties(
    apiKey: string,
    accessToken: string,
    workspaceId: string,
    boardId: string
) : Promise<IApiBoardProperty[]> {
    const url: string =
        ApiRouteFormatter.FormatMeBoardPropertiesUrl(isCloudRunEnabled(), getApiVersion(), workspaceId, boardId);
    const { properties }: { properties: IApiBoardProperty[] } = await FetchJson(EMethod.Get, url, _CreateApiRequestHeaders(apiKey, accessToken));
    return properties;
}

//
// --- Board cells

// GET https://api.kabandy.com/v1/workspaces/:workspaceId/boards/:boardId/cells?items=ITEM_ID_A,ITEM_ID_B,...&properties=PROPERTY_ID_A,PROPERTY_ID_B,...
export async function GetMeBoardCells(
    apiKey: string,
    accessToken: string,
    workspaceId: string,
    boardId: string,
    itemIds: string[] = [],
    propertyIds: string[] = []
) : Promise<IApiBoardCell[][]> {
    const itemsUrlParam: string = itemIds.join( "," );
    const propertiesUrlParam: string = propertyIds.join( "," );
    const url: string =
        ApiRouteFormatter.FormatMeBoardCellsUrl(isCloudRunEnabled(), getApiVersion(), workspaceId, boardId) + `?items=${itemsUrlParam}&properties=${propertiesUrlParam}`;
    const { cells }: { cells: IApiBoardCell[][] } = await FetchJson(EMethod.Get, url, _CreateApiRequestHeaders(apiKey, accessToken));
    return cells;
}

//
// --- API Headers

function _CreateApiRequestHeaders(apiKey?: string, token?: string): { [key: string]: string } {
    let headers: { [key: string]: string } = {};

    headers[EHeaders.ContentType] = ContentType.Json;
    
    if( apiKey ) {
        headers[EHeaders.XApiKey] = apiKey;
    }

    if (token) {
        headers[EHeaders.Authorization] = "Bearer " + token;
    }

    return headers;
}

